import { useIndexedDb } from '../indexedb/useIndexedDb'
import { parseErrorResponse, sanitizeFilename } from '@ztechapps/common.utils'
import { useState } from 'react'

const DB_NAME = 'fs-access'
const STORE_NAME = 'handles'
const DIR_KEY = 'defaultExportDir'

function supportsFsAccess() {
  return typeof window !== 'undefined' && typeof (window as any).showDirectoryPicker === 'function' && isSecureContext
}

async function ensurePermission(handle: any): Promise<boolean> {
  if (!handle) return false
  const qp = await handle.queryPermission?.({ mode: 'readwrite' })
  if (qp === 'granted') return true
  const rp = await handle.requestPermission?.({ mode: 'readwrite' })
  return rp === 'granted'
}

function useDirectoryHandle() {
  const idbOptions = { raw: true as const, dbName: DB_NAME, storeName: STORE_NAME }
  const [handle, setHandle] = useIndexedDb(DIR_KEY, undefined, idbOptions)

  const getOrRequestDirectoryHandle = async () => {
    if (handle) {
      const ok = await ensurePermission(handle)
      if (ok) return handle
    }
    const picked = await (window as any).showDirectoryPicker({ mode: 'readwrite' })
    const ok = await ensurePermission(picked)
    if (ok) {
      setHandle(picked)
      return picked
    }

    return undefined
  }
  return getOrRequestDirectoryHandle
}

export type UseAutoDownloadFileResult = {
  autoDownloadFile: (theUrl?: string, theFilename?: string) => Promise<string>
  loading: boolean
  fileLocation: string
}
export function useAutoDownloadFile(url?: string, filename = 'auto-save') {
  const [loading, setLoading] = useState(false)
  const [fileLocation, setFileLocation] = useState<string>('')

  const getOrRequestDirectoryHandle = useDirectoryHandle()

  const autoDownloadFile = async (theUrl?: string, theFilename = filename) => {
    try {
      setLoading(true)
      const finalUrl = theUrl || url
      if (!finalUrl) {
        throw new Error('No URL provided')
      }

      if (!supportsFsAccess()) {
        throw new Error('File System Access is disabled.')
      }
      const dirHandle = await getOrRequestDirectoryHandle()
      if (!dirHandle) {
        throw new Error('Failed to obtain directory handle')
      }
      const res = await fetch(finalUrl)
      if (!res.ok) {
        const message = await parseErrorResponse(res)
        throw new Error(message)
      }
      const blob = await res.blob()

      // Sanitize the filename to prevent potential security issues
      const safeBase = sanitizeFilename(theFilename)
      // Append the file extension to the sanitized filename
      const finalFilename = `${safeBase}.csv`

      // Create a new file handle in the directory, creating the file if it doesn't exist
      const fileHandle: FileSystemFileHandle = await dirHandle.getFileHandle(finalFilename, { create: true })
      // Get a writable stream for the file
      const writable = await fileHandle.createWritable?.({ keepExistingData: false })
      // Write the blob to the file
      await writable.write(blob)
      // Close the writable stream
      await writable.close()

      // Note: the File System Access API cannot reveal the absolute OS path.
      // We surface the directory name plus filename for user visibility.
      const _fileLocation = `${dirHandle.name}/${finalFilename}`
      setFileLocation(_fileLocation)
      return _fileLocation
    } finally {
      setLoading(false)
    }
  }

  return { autoDownloadFile, loading, fileLocation }
}
